import { useEffect, useMemo, useRef, useState } from 'react'
import { api } from '@/api'
import type { VoiceCatalog, VoiceGroup, VoiceInfo } from '@/api/types'
import { DEFAULT_VOICE_ID, FALLBACK_VOICE_GROUPS, findVoice } from '@/config/voices'

/**
 * 音色清单（`GET /api/aigc/voices`）—— 配音音色的**唯一来源**（数字人页与 `/tts` 页共用）。
 *
 * **为什么必须从接口取**：能用哪些音色是上游的事（默认引擎下是 `minimax-tts` skill
 * 里那份官方音色快照，303 条）。前端硬编码一份的话，上游删掉一条，
 * 页面照样会把它发出去 —— 症状是「用户选了 A、成片里的声音是 B」，
 * 而任务状态、画面、口型全绿，只有听了才知道。
 *
 * ⚠️ **缓存的是整份 `VoiceCatalog`，不只是 `groups`**（2026-10-06 改）：
 * 接口现在还下发 `engine` / `engineLabel` / `default` —— 因为配音引擎会换
 * （2026-10-06 曾因 apilio 网关的 MiniMax 渠道整体 500 临时切 `gpt-4o-mini-tts`，
 * 2026-10-08 改为直连官方 `minimax-direct` 并切回 MiniMax 音色），
 * 而**换引擎就是换整套音色 ID**。只缓存 groups 的话，界面既说不出此刻用的是哪个引擎，
 * 也拿不到「本引擎的默认音色」去做草稿回落。
 *
 * ⚠️ **不做成全局 Context**（原先的理由是「只有数字人一处用」）。
 * 2026-10-08 起 `/tts` 语音合成页也用它 —— 但结论不变：它服务的仍是
 * 「有音色下拉的那两页」，而不是全站。挂全局就意味着每个页面
 * （包括根本不用音色的那些）都要等这一个请求。模块级缓存已经把
 * 「两页各拉一次」收成「一次」，不需要更重的容器。
 *
 * 拉不到时降级到 `FALLBACK_VOICE_GROUPS`（它只覆盖**默认引擎**），
 * 并把 `error` 交给界面如实说出来 ——「音色清单没读到」和「音色就这 5 个」
 * 在界面上必须长得不一样。
 */
let cache: VoiceCatalog | null = null
let inflight: Promise<VoiceCatalog> | null = null

/**
 * 订阅者 —— `useVoices` 的每个挂载实例注册一个。
 *
 * 为什么需要它（2026-10-09 加）：克隆音色是**运行期新增**的（用户当场建），
 * 而这份清单是**模块级一次性缓存** —— 没有失效通道的话，用户克隆完回到
 * 「我的音色」，看到的还是空列表，只能刷新整页。这个回调就是那条通道：
 * 提交克隆之后调一次 `invalidateVoices()`，列表当场重拉。
 */
const listeners = new Set<() => void>()

/**
 * 让音色清单**下次读取时重新拉一次**，并通知所有挂载中的 `useVoices`。
 *
 * ⚠️ **由「改变了音色集合」的动作调用**（目前只有语音克隆）。
 * 别在普通的页面切换里调用 —— 那会把「一次缓存、多页复用」退化成每页一拉。
 */
export function invalidateVoices(): void {
  cache = null
  inflight = null
  listeners.forEach((fn) => fn())
}

/**
 * 兜底的那一份（接口没拉到时用）：引擎按默认引擎、默认音色取兜底清单第一条。
 *
 * ⚠️ 这里每个字段都必须与后端 `ENGINE_*` 表 + 默认引擎那一行**逐字对齐**
 * （默认引擎 = `minimax-direct`，模型 `speech-2.8-hd`，skill `minimax-tts`）。
 * 写错不会报错 —— 只会在后端不可达时，界面上悄悄报出另一个引擎的名字。
 */
const FALLBACK_CATALOG: VoiceCatalog = {
  engine: 'minimax-direct',
  engineLabel: 'MiniMax 官方直连',
  default: DEFAULT_VOICE_ID,
  model: 'speech-2.8-hd',
  skill: 'minimax-tts',
  count: FALLBACK_VOICE_GROUPS.reduce((n, g) => n + g.voices.length, 0),
  labelledCount: 0,
  groups: FALLBACK_VOICE_GROUPS,
  // 后端拉不到 = 更不可能知道有哪些克隆音色。给空数组，界面据此说「暂时看不到」，
  // 而不是编一个 `undefined` 让列表渲染时炸在 `.map` 上。
  mine: [],
}

async function loadVoices(): Promise<VoiceCatalog> {
  if (cache) return cache
  if (!inflight) {
    inflight = api
      .listVoices()
      .then((cat) => {
        // 空清单当失败处理：真源里不可能没有音色，返回空只说明后端配置坏了。
        // 直接用空清单会让下拉变成一个空格子，而且「没得选」看起来像功能没做。
        if (!cat.groups.length || !cat.groups.some((g) => g.voices.length)) {
          throw new Error('音色清单为空')
        }
        cache = cat
        return cat
      })
      .finally(() => {
        inflight = null
      })
  }
  return inflight
}

/**
 * 把「音色清单为什么没读到」翻成用户能据此行动的话。
 *
 * 来历（2026-10-07，用户贴的截图）：页面上写着
 * 「音色清单没读到（**Internal Server Error**）」—— 那是 dev server 的代理
 * 在后端不可达时回的 500 状态文本，原样贴上去，用户只会以为「音色功能坏了」，
 * 而真正要做的是把后端起起来。**报错的价值在于指向下一步动作**，
 * 一句 HTTP 状态名做不到这件事。
 *
 * ⚠️ 只翻译这一种：其余错误（比如音色清单为空）本来就是人话，不要裹一层。
 */
function explainVoicesError(e: unknown): string {
  const raw = e instanceof Error ? e.message : String(e)
  if (/internal server error|bad gateway|service unavailable/i.test(raw) || /HTTP_5\d\d/.test(raw)) {
    return '后端没响应 —— 确认后端已启动（或把 VITE_API_MODE 切成 http），然后刷新'
  }
  return raw
}

export type VoicesState = {
  groups: VoiceGroup[]
  /** 清单来源：后端实时 / 内置兜底 */
  source: 'remote' | 'fallback'
  /** 拉取失败的原因（null = 成功）。界面要如实显示，不能把兜底当全量。 */
  error: string | null
  /** 当前配音引擎（`minimax` | `openai` | `minimax-direct`）—— 界面上要说出来，否则「这些音色是谁的声音」无从判断 */
  engine: string
  /** 引擎展示名（如「MiniMax 官方直连」） */
  engineLabel: string
  /** 本引擎实际调用的合成模型（如 `speech-2.8-hd`）—— 花钱前那一栏要报出来 */
  model: string
  /** 实际执行合成的 skill 名（如 `minimax-tts`）—— 排查用，界面上说明「这一档由谁合成」 */
  skill: string
  /** 本引擎的默认音色 —— 草稿里的旧 ID 在本清单里不存在时回落用它 */
  defaultId: string
  /** 清单里一共多少条音色（接口下发；兜底时为兜底条数） */
  count: number
  /** 用户克隆的音色（「我的音色」页签）；拉不到时为空数组 */
  mine: VoiceInfo[]
  /** 按 ID 找音色；找不到返回 undefined */
  byId: (id: string) => { id: string; name: string } | undefined
  /** 该 ID 是不是一个真实存在的音色（用于「草稿里的值还作不作数」） */
  has: (id: string) => boolean
}

export function useVoices(): VoicesState {
  const [cat, setCat] = useState<VoiceCatalog>(cache ?? FALLBACK_CATALOG)
  const [source, setSource] = useState<'remote' | 'fallback'>(cache ? 'remote' : 'fallback')
  const [error, setError] = useState<string | null>(null)
  const alive = useRef(true)
  /**
   * 失效计数器：`invalidateVoices()` 每调一次它 +1，下面的 effect 依赖它重拉。
   * 用计数器而不是直接 `setCat(null)` —— 后者会让界面在重拉期间闪回兜底清单
   * （「刚才那 303 条呢？」），而这里保持旧数据直到新数据回来。
   */
  const [tick, setTick] = useState(0)

  useEffect(() => {
    const fn = () => setTick((t) => t + 1)
    listeners.add(fn)
    return () => {
      listeners.delete(fn)
    }
  }, [])

  useEffect(() => {
    alive.current = true
    // 已经拉到过就不再打第二次（同一个 session 里多处用同一份）；
    // 被 `invalidateVoices()` 清掉之后 cache 为空，这里会重新拉。
    if (cache) return
    loadVoices()
      .then((fresh) => {
        if (!alive.current) return
        setCat(fresh)
        setSource('remote')
        setError(null)
      })
      .catch((e: unknown) => {
        if (!alive.current) return
        setSource('fallback')
        setError(explainVoicesError(e))
      })
    return () => {
      alive.current = false
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tick])

  return useMemo<VoicesState>(() => {
    const groups = cat.groups
    const mine = Array.isArray(cat.mine) ? cat.mine : []
    // 查名字 / 判存在都要把克隆音色算上：用户选中一个克隆音色后，
    // 若这里只看系统音色，`byId` 会找不到它、界面回落成显示原始 `hwclone…` ID。
    const byIdRaw = (id: string) => findVoice(groups, id) ?? mine.find((v) => v.id === id)
    return {
      groups,
      source,
      error,
      engine: cat.engine,
      engineLabel: cat.engineLabel,
      model: cat.model,
      skill: cat.skill,
      defaultId: cat.default,
      // 兜底目录只有 5 条，而那**不是**「音色就这 5 个」—— 界面要能分开说。
      // 所以这里取 `groups` 的实际条数，界面再配上 `source`/`error` 如实交代范围。
      count: groups.reduce((n, g) => n + g.voices.length, 0),
      // ⚠️ 兜底那一份的 `mine` 恒为空 —— 它只覆盖「官方音色」，克隆音色的真源在后端库里。
      mine,
      byId: (id: string) => byIdRaw(id),
      has: (id: string) => Boolean(byIdRaw(id)),
    }
  }, [cat, source, error])
}
